import { ACTIONS } from "app/store/actions"
import { Action, Reducer } from "redux"
import { IReportForm, IReportsState } from "reports/model"
import {
  IActionReportsDelete,
  IActionReportsSetEditForm,
  IActionReportsSetFormIsUpdating,
  IActionReportsSetHasLoadingError,
  IActionReportsSetIsLoading,
  IActionReportsSetIsParametersPanelOpened,
  IActionReportsSetList,
  IActionReportsSetParameters,
  IActionReportsSetResults,
  IActionReportsUserReport,
  IActionSetRevenue
} from "./actions"
import { getDefaultReportsState } from "./state"

const getForm = (state: IReportsState, isCreation: boolean): IReportForm =>
  isCreation ? { ...state.createForm } : { ...state.editForm }
const setForm = (state: IReportsState, form: IReportForm): IReportsState =>
  form.isCreation ? { ...state, createForm: form } : { ...state, editForm: form }

export const reportsReducer: Reducer<IReportsState, Action> = (
  state: IReportsState | undefined,
  action: Action
): IReportsState => {
  if (!state) {
    state = getDefaultReportsState()
  }
  switch (action.type) {
    case ACTIONS.REPORTS_SET_PARAMETERS: {
      const { isCreation, parameters, updateFormError, isResultLoading } = action as IActionReportsSetParameters
      const form = getForm(state, isCreation)
      return setForm(state, {
        ...form,
        parameters: {
          ...parameters,
          sortBys: parameters.sortBys.filter(s => parameters.groupBys.includes(s))
        },
        isResultLoading,
        hasResultLoadingError: false,
        updateFormError
      })
    }

    case ACTIONS.REPORTS_SET_LIST: {
      const { userReports } = action as IActionReportsSetList
      return { ...state, userReports, isListLoading: false, hasListLoadingError: false }
    }

    case ACTIONS.REPORTS_SET_LIST: {
      const { userReports } = action as IActionReportsSetList
      return { ...state, userReports, isListLoading: false, hasListLoadingError: false }
    }

    case ACTIONS.REPORTS_ADD: {
      const { userReport } = action as IActionReportsUserReport
      if (!state.userReports) {
        return state
      }
      return { ...state, userReports: [...state.userReports, userReport] }
    }

    case ACTIONS.REPORTS_UPDATE: {
      const { userReport } = action as IActionReportsUserReport
      if (!state.userReports) {
        return state
      }
      return {
        ...state,
        userReports: state.userReports.map(r => {
          if (r.id === userReport.id) {
            return userReport
          }
          return r
        }),
        editForm: {
          ...state.editForm,
          isUpdating: false,
          report: userReport
        }
      }
    }

    case ACTIONS.REPORTS_DELETE: {
      const { userReportId } = action as IActionReportsDelete
      if (!state.userReports) {
        return state
      }
      return { ...state, userReports: state.userReports.filter(r => r.id !== userReportId) }
    }

    case ACTIONS.REPORTS_SET_RESULTS: {
      const { isCreation, results, parameters } = action as IActionReportsSetResults
      const form = getForm(state, isCreation)
      return setForm(state, {
        ...form,
        results,
        isResultLoading: false,
        hasResultLoadingError: false
      })
    }

    case ACTIONS.REPORTS_SET_IS_PARAMETERS_PANEL_OPENED: {
      const { isCreation, isParametersPanelOpened } = action as IActionReportsSetIsParametersPanelOpened
      const form = getForm(state, isCreation)
      return setForm(state, {
        ...form,
        isParametersPanelOpened
      })
    }

    case ACTIONS.REPORTS_SET_RESULT_IS_LOADING: {
      const { isCreation, isLoading } = action as IActionReportsSetIsLoading
      const form = getForm(state, isCreation)
      return setForm(state, {
        ...form,
        isResultLoading: isLoading,
        hasResultLoadingError: false,
        results: null
      })
    }

    case ACTIONS.REPORTS_SET_RESULT_HAS_LOADING_ERROR: {
      const { isCreation, hasLoadingError } = action as IActionReportsSetHasLoadingError
      const form = getForm(state, isCreation)
      return setForm(state, {
        ...form,
        isResultLoading: false,
        hasResultLoadingError: hasLoadingError
      })
    }

    case ACTIONS.REPORTS_SET_LIST_IS_LOADING: {
      const { isLoading } = action as IActionReportsSetIsLoading
      return { ...state, isListLoading: isLoading, hasListLoadingError: false }
    }

    case ACTIONS.REPORTS_SET_LIST_HAS_LOADING_ERROR: {
      const { hasLoadingError } = action as IActionReportsSetHasLoadingError
      return { ...state, isListLoading: false, hasListLoadingError: hasLoadingError }
    }

    case ACTIONS.REPORTS_SET_EDIT_FORM: {
      const { form } = action as IActionReportsSetEditForm
      return { ...state, editForm: form }
    }

    case ACTIONS.REPORTS_SET_FORM_IS_UPDATING: {
      const { isUpdating } = action as IActionReportsSetFormIsUpdating
      return {
        ...state,
        editForm: {
          ...state.editForm,
          isUpdating
        }
      }
    }

    case ACTIONS.REVENUE_START_LOADING: {
      return {
        ...state,
        revenue: {
          ...state.revenue,
          isLoading: true,
          hasLoadingError: false
        }
      }
    }

    case ACTIONS.REVENUE_SET: {
      const { chartDataSets, total } = action as IActionSetRevenue
      return {
        ...state,
        revenue: {
          ...state.revenue,
          chartDataSets,
          total,
          isLoading: false,
          hasLoadingError: false
        }
      }
    }

    case ACTIONS.REVENUE_SET_ERROR: {
      return {
        ...state,
        revenue: {
          ...state.revenue,
          isLoading: false,
          hasLoadingError: true
        }
      }
    }

    default:
      return state
  }
}
